/**
 * Prices API service with TanStack React Query hooks
 */

import { useQuery, useMutation } from '@tanstack/react-query'
import { apiGet, apiPost } from './client'
import type { GoldPriceHistory } from '@/types/price'
import type { VolatilityResponse } from '@/types/prices/volatility'

/**
 * Query keys for prices
 */
export const priceKeys = {
  all: ['prices'] as const,
  lists: () => [...priceKeys.all, 'list'] as const,
  list: (filters?: {
    startDate?: string
    endDate?: string
    goldTypeId?: number
    branchName?: string
  }) => [...priceKeys.lists(), filters] as const,
  volatility: (filters?: VolatilityFilters) => [...priceKeys.all, 'volatility', filters] as const,
}

export interface PriceHistoryFilters {
  startDate?: string
  endDate?: string
  goldTypeId?: number
  branchName?: string
}

export interface VolatilityFilters {
  startDate?: string
  endDate?: string
  assetTypes?: string[]
  swingThresholdPct?: number
}

/**
 * Fetch price history with optional filters
 */
export function usePriceHistory(filters?: PriceHistoryFilters) {
  return useQuery({
    queryKey: priceKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams()
      if (filters?.startDate) params.append('startDate', filters.startDate)
      if (filters?.endDate) params.append('endDate', filters.endDate)
      if (filters?.goldTypeId) params.append('goldTypeId', filters.goldTypeId.toString())
      if (filters?.branchName) params.append('branchName', filters.branchName)

      const queryString = params.toString()
      const url = `/api/prices${queryString ? `?${queryString}` : ''}`
      const response = await apiGet<{ data: GoldPriceHistory[] }>(url)
      return response.data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

/**
 * Fetch price volatility (daily movement with deltas)
 */
export function usePriceVolatility(filters?: VolatilityFilters) {
  return useQuery({
    queryKey: priceKeys.volatility(filters),
    queryFn: async (): Promise<VolatilityResponse> => {
      const params = new URLSearchParams()
      if (filters?.startDate) params.append('startDate', filters.startDate)
      if (filters?.endDate) params.append('endDate', filters.endDate)
      if (filters?.assetTypes?.length) params.append('assetTypes', filters.assetTypes.join(','))
      if (typeof filters?.swingThresholdPct === 'number') {
        params.append('swingThresholdPct', filters.swingThresholdPct.toString())
      }

      const queryString = params.toString()
      const url = `/api/prices/volatility${queryString ? `?${queryString}` : ''}`
      return apiGet<VolatilityResponse>(url)
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Response from price collection trigger endpoint
 */
export interface TriggerPriceCollectionResponse {
  data: {
    success: boolean
    date: string
    stored: number
    errors: number
    total: number
    message: string
  }
}

/**
 * Error response from price collection trigger endpoint
 */
export interface TriggerPriceCollectionError {
  error: string
  message: string
}

/**
 * Hook to trigger manual price collection
 * 
 * @example
 * ```tsx
 * const { mutate, isPending } = useTriggerPriceCollection()
 * 
 * const handleTrigger = () => {
 *   mutate(undefined, {
 *     onSuccess: (data) => {
 *       console.log(`Collected ${data.data.stored} prices`)
 *     },
 *     onError: (error) => {
 *       console.error('Collection failed:', error.message)
 *     }
 *   })
 * }
 * ```
 */
export function useTriggerPriceCollection() {
  return useMutation({
    mutationFn: async (): Promise<TriggerPriceCollectionResponse> => {
      try {
        const response = await apiPost<TriggerPriceCollectionResponse>('/api/prices/collect', {})
        return response
      } catch (err) {
        // Extract user-friendly error message from API response
        if (err instanceof Error) {
          // Try to parse error response if available
          const errorMessage = err.message
          throw new Error(errorMessage)
        }
        throw new Error('Đã xảy ra lỗi khi thu thập giá. Vui lòng thử lại sau.')
      }
    },
  })
}

